import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Organization types - determines the features and views available
 * artist: Creates/owns content (studios), uploads masters, shares for review
 * client: Receives/uses content (agencies), approves spots, licenses IN
 * broadcaster: Radio stations/media houses, receives approved masters, downloads
 */
export type OrganizationType = 'artist' | 'client' | 'broadcaster';

/**
 * Organization category - more specific classification for display purposes
 * Maps to OrganizationType:
 *   artist, publisher → 'artist'
 *   agency, brand, production → 'client'
 */
export type OrgCategory = 'artist' | 'publisher' | 'agency' | 'brand' | 'production' | 'broadcaster' | 'mediahouse';

/**
 * Maps category to organization type
 */
export const categoryToType = (category: OrgCategory): OrganizationType => {
  switch (category) {
    case 'artist':
    case 'publisher':
      return 'artist';
    case 'agency':
    case 'brand':
    case 'production':
      return 'client';
    case 'broadcaster':
    case 'mediahouse':
      return 'broadcaster';
    default:
      return 'artist';
  }
};

/**
 * Get display label for category.
 *
 * NOTE: the underlying Firestore value for category 'artist' and type
 * 'artist' stays unchanged — we only rename the user-facing label to
 * "Studio". Every place in the UI that needs to display the category or
 * type should funnel through this helper (or `getTypeLabel` below) so a
 * future label change is a one-line edit.
 */
export const getCategoryLabel = (category?: OrgCategory | null, type?: OrganizationType | null): string => {
  if (category) {
    switch (category) {
      case 'artist': return 'Studio';
      case 'publisher': return 'Publisher';
      case 'agency': return 'Agency';
      case 'brand': return 'Brand';
      case 'production': return 'Production';
      case 'broadcaster': return 'Broadcaster';
      case 'mediahouse': return 'Media House';
    }
  }
  // Fallback to type for legacy orgs (no category field).
  if (type === 'client') return 'Client';
  if (type === 'broadcaster') return 'Broadcaster';
  return 'Studio';
};

/**
 * Get display label for a raw OrganizationType. Use this when the caller
 * only has the top-level `type` field (e.g. an /organizations doc with no
 * category set, or the derived type shown in admin tables).
 */
export const getTypeLabel = (type?: OrganizationType | string | null): string => {
  if (type === 'client') return 'Client';
  if (type === 'broadcaster') return 'Broadcaster';
  return 'Studio';
};

/**
 * Organization membership structure
 * Represents a user's membership in an organization with their role
 */
export interface OrganizationMembership {
  orgId: string;
  orgName: string;
  orgType?: OrganizationType;  // Optional for backwards compatibility during migration
  orgCategory?: OrgCategory;   // Optional - specific category for display
  role: 'ADMIN' | 'OPERATOR' | 'USER';
  joinedAt?: string;
}

/**
 * Organization document structure in Firestore
 */
export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;  // 'artist' or 'client'
  category?: OrgCategory;  // Optional - specific category for display (legacy orgs won't have this)
  createdAt: string;
  createdBy: string;
  settings?: {
    allowUserInvites?: boolean;
    defaultUserRole?: 'OPERATOR' | 'USER';
  };
}

/**
 * Organization store state interface
 */
interface OrganizationState {
  // Current active organization
  currentOrgId: string | null;
  currentOrgName: string | null;
  currentOrgType: OrganizationType | null;
  currentOrgCategory: OrgCategory | null;
  currentOrgRole: 'ADMIN' | 'OPERATOR' | 'USER' | null;
  currentOrgLogoUrl: string | null;
  /** Delivery overrides — only Master Orgs (and any future org class we open
   *  this up to) can toggle these. When on, the matching delivery gate
   *  (QC pass / Review approval) is bypassed in JobDetail.handleShareJob. */
  currentOrgBypassQc: boolean;
  currentOrgBypassReview: boolean;
  /** Ordering rule — when FALSE (default), spots must pass QC before they
   *  can be shared for review. When TRUE, the QC-before-Review check is
   *  skipped and spots can be shared in any order. Independent from the
   *  delivery overrides above. */
  currentOrgBypassQcBeforeReview: boolean;
  /** Whether this org is allowed to manage Delivery Overrides. Granted per-org
   *  by a Master-Org admin / super-admin via the Delivery Overrides admin grid
   *  (organizations/{orgId}.deliveryOverridesEnabled). When true, the org sees
   *  the DeliveryOverridesEditor on its own /admin/organization page. */
  currentOrgDeliveryOverridesEnabled: boolean;

  // All organizations the user belongs to
  organizations: OrganizationMembership[];

  // Super admin status (can access all organizations)
  isSuperAdmin: boolean;

  // Master org status (has access to Master Library and Public Library)
  isMasterOrg: boolean;

  // Loading state
  isLoading: boolean;

  // Actions
  setCurrentOrganization: (orgId: string, orgName: string, role: 'ADMIN' | 'OPERATOR' | 'USER', type?: OrganizationType, category?: OrgCategory) => void;
  setCurrentOrgLogo: (logoUrl: string | null) => void;
  setCurrentOrgDeliveryOverrides: (overrides: { bypassQc: boolean; bypassReview: boolean; bypassQcBeforeReview?: boolean }) => void;
  setCurrentOrgDeliveryOverridesEnabled: (enabled: boolean) => void;
  setOrganizations: (orgs: OrganizationMembership[]) => void;
  setSuperAdmin: (isSuperAdmin: boolean) => void;
  setMasterOrg: (isMasterOrg: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  clearOrganization: () => void;

  // Computed helpers
  hasMultipleOrgs: () => boolean;
  canManageOrg: () => boolean;
  canEditTracks: () => boolean;
  isArtistOrg: () => boolean;
  isClientOrg: () => boolean;
  getCategoryLabel: () => string;
}

/**
 * Organization store using Zustand with persistence
 * Persists currentOrgId to localStorage so it's remembered across sessions
 */
export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentOrgId: null,
      currentOrgName: null,
      currentOrgType: null,
      currentOrgCategory: null,
      currentOrgRole: null,
      currentOrgLogoUrl: null,
      currentOrgBypassQc: false,
      currentOrgBypassReview: false,
      currentOrgBypassQcBeforeReview: false,
      currentOrgDeliveryOverridesEnabled: false,
      organizations: [],
      isSuperAdmin: false,
      isMasterOrg: false,
      isLoading: true, // Start as true, will be set to false after org data loads

      // Set the current active organization
      setCurrentOrganization: (orgId, orgName, role, type, category) => {
        set({
          currentOrgId: orgId,
          currentOrgName: orgName,
          currentOrgType: type || 'artist',  // Default to 'artist' for backwards compatibility
          currentOrgCategory: category || null,
          currentOrgRole: role,
          currentOrgLogoUrl: null,  // Cleared until the org doc is fetched and the URL is loaded
          currentOrgBypassQc: false,
          currentOrgBypassReview: false,
          currentOrgBypassQcBeforeReview: false,
          currentOrgDeliveryOverridesEnabled: false,
        });
      },

      setCurrentOrgLogo: (logoUrl) => set({ currentOrgLogoUrl: logoUrl }),

      setCurrentOrgDeliveryOverrides: ({ bypassQc, bypassReview, bypassQcBeforeReview }) =>
        set({
          currentOrgBypassQc: !!bypassQc,
          currentOrgBypassReview: !!bypassReview,
          currentOrgBypassQcBeforeReview: !!bypassQcBeforeReview,
        }),

      setCurrentOrgDeliveryOverridesEnabled: (enabled) =>
        set({ currentOrgDeliveryOverridesEnabled: !!enabled }),

      // Set all organizations the user belongs to
      setOrganizations: (orgs) => {
        set({ organizations: orgs });

        const state = get();

        // Only auto-select if user has exactly ONE org and no org is currently set
        // For multiple orgs, user must explicitly choose via SelectOrganization page
        if (!state.currentOrgId && orgs.length === 1) {
          set({
            currentOrgId: orgs[0].orgId,
            currentOrgName: orgs[0].orgName,
            currentOrgType: orgs[0].orgType || 'artist',
            currentOrgCategory: orgs[0].orgCategory || null,
            currentOrgRole: orgs[0].role,
            currentOrgLogoUrl: null,
            currentOrgBypassQc: false,
            currentOrgBypassReview: false,
            currentOrgBypassQcBeforeReview: false,
          });
        }

        // If current org is set, update the role/name/type/category from the new orgs list
        if (state.currentOrgId) {
          const currentOrg = orgs.find(o => o.orgId === state.currentOrgId);
          if (currentOrg) {
            set({
              currentOrgName: currentOrg.orgName,
              currentOrgType: currentOrg.orgType || 'artist',
              currentOrgCategory: currentOrg.orgCategory || null,
              currentOrgRole: currentOrg.role,
            });
          } else if (orgs.length === 1) {
            // Current org no longer valid and user has only one org, switch to it
            set({
              currentOrgId: orgs[0].orgId,
              currentOrgName: orgs[0].orgName,
              currentOrgType: orgs[0].orgType || 'artist',
              currentOrgCategory: orgs[0].orgCategory || null,
              currentOrgRole: orgs[0].role,
              currentOrgLogoUrl: null,
              currentOrgBypassQc: false,
              currentOrgBypassReview: false,
            });
          } else if (orgs.length > 1) {
            // Current org no longer valid but user has multiple orgs
            // Clear selection so they go to SelectOrganization page
            set({
              currentOrgId: null,
              currentOrgName: null,
              currentOrgType: null,
              currentOrgCategory: null,
              currentOrgRole: null,
              currentOrgLogoUrl: null,
              currentOrgBypassQc: false,
              currentOrgBypassReview: false,
            });
          }
        }
      },

      // Set super admin status
      setSuperAdmin: (isSuperAdmin) => set({ isSuperAdmin }),
      setMasterOrg: (isMasterOrg) => set({ isMasterOrg }),

      // Set loading state
      setLoading: (isLoading) => set({ isLoading }),

      // Clear organization state (on logout)
      // Note: Don't reset isLoading here - it should stay true until auth flow completes
      clearOrganization: () => set({
        currentOrgId: null,
        currentOrgName: null,
        currentOrgType: null,
        currentOrgCategory: null,
        currentOrgRole: null,
        currentOrgLogoUrl: null,
        currentOrgBypassQc: false,
        currentOrgBypassReview: false,
        currentOrgBypassQcBeforeReview: false,
        currentOrgDeliveryOverridesEnabled: false,
        organizations: [],
        isSuperAdmin: false,
        isMasterOrg: false,
      }),

      // Check if user belongs to multiple organizations
      hasMultipleOrgs: () => {
        const state = get();
        return state.organizations.length > 1 || state.isSuperAdmin;
      },

      // Check if user can manage the current organization (ADMIN role)
      canManageOrg: () => {
        const state = get();
        return state.isSuperAdmin || state.currentOrgRole === 'ADMIN';
      },

      // Check if user can edit tracks (ADMIN or OPERATOR)
      canEditTracks: () => {
        const state = get();
        return state.isSuperAdmin ||
               state.currentOrgRole === 'ADMIN' ||
               state.currentOrgRole === 'OPERATOR';
      },

      // Check if current organization is an artist org
      isArtistOrg: () => {
        const state = get();
        return state.currentOrgType === 'artist' || !state.currentOrgType;  // Default to artist
      },

      // Check if current organization is a client org
      isClientOrg: () => {
        const state = get();
        return state.currentOrgType === 'client';
      },

      // Check if current organization is a broadcaster org
      isBroadcasterOrg: () => {
        const state = get();
        return state.currentOrgType === 'broadcaster';
      },

      // Get display label for current org's category
      getCategoryLabel: () => {
        const state = get();
        return getCategoryLabel(state.currentOrgCategory, state.currentOrgType);
      },
    }),
    {
      name: 'vault-organization-storage',
      // Only persist certain fields
      partialize: (state) => ({
        currentOrgId: state.currentOrgId,
      }),
    }
  )
);

/**
 * Helper function to get organization-scoped collection path
 * @param orgId - Organization ID
 * @param collectionName - Name of the collection
 * @returns Full collection path
 */
export const getOrgCollectionPath = (orgId: string, collectionName: string): string => {
  return `organizations/${orgId}/${collectionName}`;
};

/**
 * Helper function to get organization-scoped storage path
 * @param orgId - Organization ID
 * @param trackId - Track ID
 * @returns Storage path prefix for the track
 */
export const getOrgStoragePath = (orgId: string, trackId: string): string => {
  return `audio/${orgId}/${trackId}`;
};

/**
 * Collections that are organization-scoped (private to each org)
 */
export const ORG_SCOPED_COLLECTIONS = [
  'audiometa',
  'backuplocation',
  'composers',
  'creativedirector',
  'flightingplatform',
  'producer',
  'sessionartists',
  'studio',
] as const;

/**
 * Collections that are global (shared across all orgs)
 */
export const GLOBAL_COLLECTIONS = [
  'tags',
  'keys',
  'bpm',
  'genres',
  'mappings',
  'users',
  'agency',   // Global for cross-org normalization
  'client',   // Global for cross-org normalization
] as const;

export type OrgScopedCollection = typeof ORG_SCOPED_COLLECTIONS[number];
export type GlobalCollection = typeof GLOBAL_COLLECTIONS[number];
